import React, { useCallback, useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import {
  FilePlus2, FileText, Loader2, RefreshCw, Search, UploadCloud,
} from "lucide-react";

import { api, errMsg } from "@/lib/api";
import {
  canAccessBriefIntelligence,
  canChangeClearance,
  canWrite,
  useAuth,
} from "@/lib/auth";
import EmptyState, { PageHeader } from "@/components/Common";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Card, CardContent, CardDescription, CardHeader, CardTitle,
} from "@/components/ui/card";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter,
  DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Progress } from "@/components/ui/progress";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Textarea } from "@/components/ui/textarea";

const ALLOWED_EXTENSIONS = [".pdf", ".docx", ".txt", ".md", ".markdown"];
const MAX_UPLOAD_BYTES = 25 * 1024 * 1024;

function formatDate(value) {
  if (!value) return "\u2014";
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? "\u2014" : date.toLocaleString();
}

function formatBytes(value) {
  if (!Number.isFinite(value) || value < 0) return "\u2014";
  if (value < 1024) return `${value} B`;
  if (value < 1024 * 1024) return `${(value / 1024).toFixed(1)} KB`;
  return `${(value / (1024 * 1024)).toFixed(1)} MB`;
}

function extensionOf(filename) {
  const lower = String(filename || "").toLowerCase();
  const dot = lower.lastIndexOf(".");
  return dot >= 0 ? lower.slice(dot) : "";
}

function duplicateDetail(error) {
  const detail = error?.response?.data?.detail;
  return (
    error?.response?.status === 409
    && detail
    && typeof detail === "object"
    && detail.suggest === "confirm_duplicate"
  ) ? detail : null;
}

// Correction 2 — string-safe error handling. errMsg(...) may return a
// structured detail object. This helper always returns a printable string.
function safeErrorText(error, fallback) {
  const detail = error?.response?.data?.detail;

  if (typeof detail === "string" && detail.trim()) {
    return detail;
  }

  if (
    detail
    && typeof detail === "object"
    && !Array.isArray(detail)
    && typeof detail.message === "string"
    && detail.message.trim()
  ) {
    return detail.message;
  }

  if (Array.isArray(detail)) {
    const messages = detail
      .map((item) => item?.msg)
      .filter((message) => typeof message === "string" && message.trim());

    if (messages.length) {
      return messages.join(" ");
    }
  }

  const resolved = errMsg(error, fallback);
  return typeof resolved === "string" && resolved.trim()
    ? resolved
    : fallback;
}

// Correction 3 — do not label every 403 as a duplicate-confirmation denial.
function safeCreateError(error, fallback, { duplicateRetry = false } = {}) {
  const status = error?.response?.status;

  if (status === 413) {
    return "The selected file is larger than the 25 MB limit.";
  }
  if (status === 415) {
    return "That file type is not supported.";
  }
  if (status === 502) {
    return "Storage service unavailable. Try again.";
  }
  if (status === 403 && duplicateRetry) {
    return "You are not permitted to confirm this duplicate.";
  }
  if (status === 403) {
    return "You are not permitted to perform this Brief Intelligence action.";
  }
  return safeErrorText(error, fallback);
}

// Correction 4 — minimise list data before state. Never retain an unrestricted
// persisted brief document in frontend memory; whitelist top-level and source
// fields explicitly.
function projectBriefListRow(row) {
  const source = row?.source || {};
  return {
    id: row?.id || "",
    ref_id: row?.ref_id || "",
    title: row?.title || "",
    workflow_status: row?.workflow_status || "",
    source: {
      filename: source.filename || "",
      source_kind: source.source_kind || "",
      size_bytes: Number.isFinite(source.size_bytes)
        ? source.size_bytes
        : null,
      source_text_preview: source.source_text_preview || "",
    },
    created_at: row?.created_at || "",
    updated_at: row?.updated_at || "",
    // Assignment 2K — minimal review-progress metadata only.
    review_progress: projectReviewProgress(row?.review_progress),
  };
}

// Assignment 2K — bounded projection of the desk progress indicator.
// Returns null when the backend omitted progress (fail-neutral: render
// nothing rather than a misleading number).
function projectReviewProgress(raw) {
  if (!raw || typeof raw !== "object") return null;
  if (raw.state === "ready") {
    const total = Number.isFinite(raw.total) ? raw.total : 0;
    if (total <= 0) return { state: "not_ready" };
    const reviewed = Math.max(0, Math.min(
      Number.isFinite(raw.reviewed) ? raw.reviewed : 0, total,
    ));
    return {
      state: "ready",
      reviewed,
      total,
      percent: Math.round((reviewed * 100) / total),
    };
  }
  if (raw.state === "not_ready") return { state: "not_ready" };
  return null;
}

// Assignment 2K — compact per-row review progress. Visibility only.
function ReviewProgressIndicator({ progress, briefId }) {
  if (!progress) return null;
  if (progress.state !== "ready") {
    return (
      <span
        className="text-[11px] italic text-muted-foreground whitespace-nowrap"
        data-testid={`brief-row-review-progress-${briefId}`}
      >
        Not ready for review
      </span>
    );
  }
  return (
    <div
      className="w-36 shrink-0 space-y-1"
      data-testid={`brief-row-review-progress-${briefId}`}
    >
      <div className="flex items-center justify-between text-[11px] text-muted-foreground">
        <span data-testid={`brief-row-review-count-${briefId}`}>
          Review: {progress.reviewed} / {progress.total}
        </span>
        <span data-testid={`brief-row-review-percent-${briefId}`}>
          {progress.percent}%
        </span>
      </div>
      <Progress
        value={progress.percent}
        className="h-1.5"
        data-testid={`brief-row-review-bar-${briefId}`}
      />
    </div>
  );
}

// Correction 5 — minimise duplicate response data. Only the fields the dialog
// actually displays are stored.
function projectDuplicateDetail(detail) {
  const existing = detail?.existing || {};
  return {
    suggest: "confirm_duplicate",
    existing: {
      existing_brief_id: existing.existing_brief_id || "",
      existing_ref_id: existing.existing_ref_id || "",
      existing_title: existing.existing_title || "",
      existing_workflow_status: existing.existing_workflow_status || "",
      created_at: existing.created_at || "",
      created_by_email: existing.created_by_email || "",
    },
  };
}

function WorkflowBadge({ value, testid }) {
  return (
    <Badge
      variant="outline"
      className="text-[10px] uppercase tracking-[0.14em] font-semibold"
      data-testid={testid}
    >
      {value || "\u2014"}
    </Badge>
  );
}

function DuplicateDialog({
  duplicate,
  canOverride,
  busy,
  onOpenExisting,
  onCreateAnyway,
  onCancel,
}) {
  const existing = duplicate?.detail?.existing || {};
  return (
    <Dialog open={Boolean(duplicate)} onOpenChange={(open) => { if (!open) onCancel(); }}>
      <DialogContent data-testid="brief-duplicate-dialog">
        <DialogHeader>
          <DialogTitle>Duplicate confirmation required</DialogTitle>
          <DialogDescription>
            An active brief with the same source content already exists.
            No duplicate has been created.
          </DialogDescription>
        </DialogHeader>
        <div className="grid sm:grid-cols-2 gap-3 text-[13px]">
          <div>
            <div className="text-[10px] uppercase tracking-wide text-muted-foreground">Existing title</div>
            <div data-testid="brief-duplicate-existing-title">
              {existing.existing_title || "\u2014"}
            </div>
          </div>
          <div>
            <div className="text-[10px] uppercase tracking-wide text-muted-foreground">Reference</div>
            <div className="font-mono" data-testid="brief-duplicate-existing-reference">
              {existing.existing_ref_id || "\u2014"}
            </div>
          </div>
          <div>
            <div className="text-[10px] uppercase tracking-wide text-muted-foreground">Workflow status</div>
            <div data-testid="brief-duplicate-existing-status">
              {existing.existing_workflow_status || "\u2014"}
            </div>
          </div>
          <div>
            <div className="text-[10px] uppercase tracking-wide text-muted-foreground">Created</div>
            <div data-testid="brief-duplicate-existing-created">
              {formatDate(existing.created_at)}
            </div>
          </div>
          <div className="sm:col-span-2">
            <div className="text-[10px] uppercase tracking-wide text-muted-foreground">Created by</div>
            <div data-testid="brief-duplicate-existing-created-by">
              {existing.created_by_email || "\u2014"}
            </div>
          </div>
        </div>
        {!canOverride && (
          <Alert>
            <AlertDescription>
              Your role may create ordinary briefs but cannot override duplicate
              confirmation. Open the existing brief or contact a GFMP Reviewer
              or Super Admin.
            </AlertDescription>
          </Alert>
        )}
        <DialogFooter className="gap-2 sm:gap-0">
          <Button
            variant="outline"
            onClick={onCancel}
            disabled={busy}
            data-testid="brief-duplicate-cancel"
          >
            Cancel
          </Button>
          <Button
            variant="outline"
            onClick={() => onOpenExisting(existing.existing_brief_id)}
            disabled={busy || !existing.existing_brief_id}
            data-testid="brief-duplicate-open-existing"
          >
            Open Existing Brief
          </Button>
          {canOverride && (
            <Button
              onClick={onCreateAnyway}
              disabled={busy}
              data-testid="brief-duplicate-create-anyway"
            >
              {busy && <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" />}
              Create Anyway
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default function BriefsDesk() {
  const { user } = useAuth();
  const canRead = canAccessBriefIntelligence(user);
  const writer = canWrite(user);
  const canOverrideDuplicate = canChangeClearance(user);
  const navigate = useNavigate();

  const [rows, setRows] = useState([]);
  // Correction 1 — initialise the first request as loading.
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState("");
  const [q, setQ] = useState("");
  const [appliedQ, setAppliedQ] = useState("");

  const [createOpen, setCreateOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [textForm, setTextForm] = useState({
    title: "",
    source_text: "",
    source_filename: "",
  });
  const [uploadTitle, setUploadTitle] = useState("");
  const [uploadFile, setUploadFile] = useState(null);
  const [fileInputKey, setFileInputKey] = useState(0);
  const [duplicate, setDuplicate] = useState(null);

  const load = useCallback(async () => {
    if (!canRead) return;
    setLoading(true);
    setLoadError("");
    try {
      const params = { limit: 200 };
      if (appliedQ) params.q = appliedQ;
      const response = await api.get("/briefs", { params });
      const rawRows = Array.isArray(response.data?.rows) ? response.data.rows : [];
      setRows(rawRows.map(projectBriefListRow));
    } catch (error) {
      setLoadError(safeErrorText(error, "Could not load briefs."));
    } finally {
      setLoading(false);
    }
  }, [appliedQ, canRead]);

  useEffect(() => {
    if (canRead) load();
  }, [canRead, load]);

  function resetCreateState() {
    setTextForm({ title: "", source_text: "", source_filename: "" });
    setUploadTitle("");
    setUploadFile(null);
    setFileInputKey((value) => value + 1);
    setDuplicate(null);
  }

  function closeCreateDialog() {
    if (busy) return;
    setCreateOpen(false);
    resetCreateState();
  }

  async function submitText(confirmDuplicate = false) {
    if (!textForm.title.trim() || !textForm.source_text.trim()) {
      toast.error("Provide a title and source text.");
      return;
    }
    setBusy(true);
    try {
      // Correction 6 — omit empty optional source_filename instead of sending null.
      const sourceFilename = textForm.source_filename.trim();
      const payload = {
        title: textForm.title.trim(),
        source_text: textForm.source_text,
        confirm_duplicate: confirmDuplicate,
        ...(sourceFilename ? { source_filename: sourceFilename } : {}),
      };
      const response = await api.post("/briefs/from-text", payload);
      const createdId = response.data?.id;
      const createdRef = response.data?.ref_id;
      setTextForm({ title: "", source_text: "", source_filename: "" });
      setDuplicate(null);
      setCreateOpen(false);
      toast.success(createdRef ? `Brief ${createdRef} created.` : "Brief created.");
      if (createdId) navigate(`/advanced/briefs/${createdId}`);
      else load();
    } catch (error) {
      const detail = duplicateDetail(error);
      if (detail) {
        // Correction 5 — store the minimised projection only.
        setDuplicate({ kind: "text", detail: projectDuplicateDetail(detail) });
        setCreateOpen(false);
      } else {
        toast.error(
          safeCreateError(error, "Could not create brief.", { duplicateRetry: confirmDuplicate }),
        );
      }
    } finally {
      setBusy(false);
    }
  }

  async function submitUpload(confirmDuplicate = false) {
    if (!uploadTitle.trim() || !uploadFile) {
      toast.error("Provide a title and file.");
      return;
    }
    const extension = extensionOf(uploadFile.name);
    if (!ALLOWED_EXTENSIONS.includes(extension)) {
      toast.error("Choose a PDF, DOCX, TXT, MD, or MARKDOWN file.");
      return;
    }
    if (uploadFile.size > MAX_UPLOAD_BYTES) {
      toast.error("The selected file is larger than the 25 MB limit.");
      return;
    }

    setBusy(true);
    try {
      const formData = new FormData();
      formData.append("title", uploadTitle.trim());
      formData.append("confirm_duplicate", confirmDuplicate ? "true" : "false");
      formData.append("file", uploadFile);
      const response = await api.post("/briefs/from-upload", formData, {
        headers: { "Content-Type": "multipart/form-data" },
        timeout: 600000,
      });
      const createdId = response.data?.id;
      const createdRef = response.data?.ref_id;
      setUploadTitle("");
      setUploadFile(null);
      setFileInputKey((value) => value + 1);
      setDuplicate(null);
      setCreateOpen(false);
      toast.success(createdRef ? `Brief ${createdRef} created.` : "Brief uploaded.");
      if (createdId) navigate(`/advanced/briefs/${createdId}`);
      else load();
    } catch (error) {
      const detail = duplicateDetail(error);
      if (detail) {
        setDuplicate({ kind: "upload", detail: projectDuplicateDetail(detail) });
        setCreateOpen(false);
      } else {
        toast.error(
          safeCreateError(error, "Could not upload brief.", { duplicateRetry: confirmDuplicate }),
        );
      }
    } finally {
      setBusy(false);
    }
  }

  if (!canRead) {
    return (
      <div className="p-6 max-w-2xl mx-auto" data-testid="briefs-permission-state">
        <Alert>
          <AlertDescription>
            Brief Intelligence is limited to authorised Kandake operators.
          </AlertDescription>
        </Alert>
      </div>
    );
  }

  return (
    <div className="p-6 max-w-6xl mx-auto space-y-4" data-testid="briefs-desk-page">
      <PageHeader
        title="Brief Intelligence"
        subtitle="A MAGI system for structured intake and extraction from client briefs. Deterministic parsing is available; automated structured extraction is not currently configured."
        actions={(
          <>
            <Button
              variant="outline"
              onClick={load}
              disabled={loading}
              data-testid="briefs-refresh"
            >
              {loading
                ? <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" />
                : <RefreshCw className="w-3.5 h-3.5 mr-1.5" />}
              Refresh
            </Button>
            {writer && (
              <Button onClick={() => setCreateOpen(true)} data-testid="briefs-new">
                <FilePlus2 className="w-3.5 h-3.5 mr-1.5" />
                New Brief
              </Button>
            )}
          </>
        )}
      />

      <Card>
        <CardContent className="p-3">
          <form
            className="flex items-center gap-2"
            onSubmit={(event) => {
              event.preventDefault();
              const nextQuery = q.trim();
              if (nextQuery === appliedQ) load();
              else setAppliedQ(nextQuery);
            }}
          >
            <div className="flex-1 min-w-[220px] relative">
              <Search className="absolute left-2 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-muted-foreground" />
              <Input
                value={q}
                onChange={(event) => setQ(event.target.value)}
                placeholder="Search title or brief reference…"
                className="pl-7 h-8 text-[12px]"
                data-testid="briefs-search"
              />
            </div>
            <Button
              type="submit"
              size="sm"
              variant="outline"
              disabled={loading}
              data-testid="briefs-search-submit"
            >
              Search
            </Button>
          </form>
        </CardContent>
      </Card>

      {loadError && (
        <Alert variant="destructive" data-testid="briefs-error-state">
          <AlertDescription>
            {loadError}
            <Button
              variant="link"
              className="h-auto ml-2 p-0"
              onClick={load}
              data-testid="briefs-retry"
            >
              Try again
            </Button>
          </AlertDescription>
        </Alert>
      )}

      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-[15px]">Briefs ({rows.length})</CardTitle>
          <CardDescription>
            Safe metadata and bounded previews only. Confidential source content
            and private storage locations are not displayed.
          </CardDescription>
        </CardHeader>
        <CardContent>
          {/* Correction 9 — do not render an empty <ul> when initial load failed. */}
          {loading ? (
            <div
              className="flex items-center gap-2 text-sm text-muted-foreground"
              data-testid="briefs-loading"
            >
              <Loader2 className="w-4 h-4 animate-spin" />
              Loading briefs…
            </div>
          ) : loadError && rows.length === 0 ? null : rows.length === 0 ? (
            <EmptyState
              icon={FileText}
              title="No briefs found"
              description={appliedQ
                ? "No briefs match this search."
                : "No Brief Intelligence records are available yet."}
            />
          ) : (
            <ul className="divide-y border rounded-md" data-testid="briefs-list">
              {rows.map((brief) => {
                const source = brief.source || {};
                return (
                  <li
                    key={brief.id}
                    className="p-3 flex items-center gap-3"
                    data-testid={`brief-row-${brief.id}`}
                  >
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span
                          className="font-mono text-[11px] text-muted-foreground"
                          data-testid={`brief-row-reference-${brief.id}`}
                        >
                          {brief.ref_id || brief.id}
                        </span>
                        <span
                          className="text-[13px] font-medium truncate"
                          data-testid={`brief-row-title-${brief.id}`}
                        >
                          {brief.title || "(untitled brief)"}
                        </span>
                      </div>
                      <div className="text-[11px] text-muted-foreground mt-1 flex flex-wrap gap-x-3">
                        <span data-testid={`brief-row-filename-${brief.id}`}>
                          File: {source.filename || "\u2014"}
                        </span>
                        <span data-testid={`brief-row-source-kind-${brief.id}`}>
                          Type: {source.source_kind || "\u2014"}
                        </span>
                        <span data-testid={`brief-row-size-${brief.id}`}>
                          Size: {formatBytes(source.size_bytes)}
                        </span>
                        <span data-testid={`brief-row-created-${brief.id}`}>
                          Created: {formatDate(brief.created_at)}
                        </span>
                        <span data-testid={`brief-row-updated-${brief.id}`}>
                          Updated: {formatDate(brief.updated_at)}
                        </span>
                      </div>
                      {source.source_text_preview && (
                        <p
                          className="text-[12px] text-muted-foreground mt-2 line-clamp-2 whitespace-pre-wrap break-words"
                          data-testid={`brief-row-preview-${brief.id}`}
                        >
                          {source.source_text_preview}
                        </p>
                      )}
                    </div>
                    <ReviewProgressIndicator
                      progress={brief.review_progress}
                      briefId={brief.id}
                    />
                    <WorkflowBadge
                      value={brief.workflow_status}
                      testid={`brief-row-status-${brief.id}`}
                    />
                    <Button
                      asChild
                      size="sm"
                      variant="ghost"
                      data-testid={`brief-open-${brief.id}`}
                    >
                      <Link to={`/advanced/briefs/${brief.id}`}>Open</Link>
                    </Button>
                  </li>
                );
              })}
            </ul>
          )}
        </CardContent>
      </Card>

      <Dialog open={createOpen} onOpenChange={(open) => { if (!open) closeCreateDialog(); }}>
        <DialogContent className="max-w-2xl" data-testid="brief-create-dialog">
          <DialogHeader>
            <DialogTitle>Create Brief</DialogTitle>
            <DialogDescription>
              Submit pasted text or an approved document. Source text is cleared
              from the form after successful submission.
            </DialogDescription>
          </DialogHeader>
          <Tabs defaultValue="text">
            <TabsList className="grid w-full grid-cols-2">
              <TabsTrigger value="text" data-testid="brief-create-tab-text">
                Pasted Text
              </TabsTrigger>
              <TabsTrigger value="upload" data-testid="brief-create-tab-upload">
                File Upload
              </TabsTrigger>
            </TabsList>
            <TabsContent value="text" className="space-y-3 mt-4">
              <div>
                <Label>Title</Label>
                <Input
                  value={textForm.title}
                  onChange={(event) => setTextForm({ ...textForm, title: event.target.value })}
                  data-testid="brief-text-title"
                />
              </div>
              <div>
                <Label>Source filename (optional)</Label>
                <Input
                  value={textForm.source_filename}
                  onChange={(event) => setTextForm({ ...textForm, source_filename: event.target.value })}
                  placeholder="client-brief.txt"
                  data-testid="brief-text-filename"
                />
              </div>
              <div>
                <Label>Source text</Label>
                <Textarea
                  value={textForm.source_text}
                  onChange={(event) => setTextForm({ ...textForm, source_text: event.target.value })}
                  rows={10}
                  data-testid="brief-text-source"
                />
              </div>
              <div className="flex justify-end">
                <Button
                  onClick={() => submitText(false)}
                  disabled={busy || !textForm.title.trim() || !textForm.source_text.trim()}
                  data-testid="brief-text-submit"
                >
                  {busy && <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" />}
                  Create Brief
                </Button>
              </div>
            </TabsContent>
            <TabsContent value="upload" className="space-y-3 mt-4">
              <div>
                <Label>Title</Label>
                <Input
                  value={uploadTitle}
                  onChange={(event) => setUploadTitle(event.target.value)}
                  data-testid="brief-upload-title"
                />
              </div>
              <div>
                <Label>Approved file</Label>
                <Input
                  key={fileInputKey}
                  type="file"
                  accept=".pdf,.docx,.txt,.md,.markdown"
                  onChange={(event) => setUploadFile(event.target.files?.[0] || null)}
                  data-testid="brief-upload-file"
                />
                <p className="text-[11px] text-muted-foreground mt-1">
                  PDF, DOCX, TXT, MD, or MARKDOWN; maximum 25 MB.
                </p>
              </div>
              <div className="flex justify-end">
                <Button
                  onClick={() => submitUpload(false)}
                  disabled={busy || !uploadTitle.trim() || !uploadFile}
                  data-testid="brief-upload-submit"
                >
                  {busy
                    ? <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" />
                    : <UploadCloud className="w-3.5 h-3.5 mr-1.5" />}
                  Upload Brief
                </Button>
              </div>
            </TabsContent>
          </Tabs>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={closeCreateDialog}
              disabled={busy}
              data-testid="brief-create-cancel"
            >
              Cancel
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <DuplicateDialog
        duplicate={duplicate}
        canOverride={canOverrideDuplicate}
        busy={busy}
        onOpenExisting={(id) => {
          if (id) navigate(`/advanced/briefs/${id}`);
        }}
        onCreateAnyway={() => {
          if (duplicate?.kind === "text") submitText(true);
          if (duplicate?.kind === "upload") submitUpload(true);
        }}
        onCancel={() => {
          setDuplicate(null);
          setCreateOpen(true);
        }}
      />
    </div>
  );
}
